import type { FC } from 'react';
import { Typography } from '@mui/material';
import { identity } from 'rxjs';

import { TripTypeEnum } from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';

import { FlexColumn } from '../../../atoms/FlexContainer/FlexColumn';
import { FlexContainer } from '../../../atoms/FlexContainer/FlexContainer';
import { PaymentBlock } from '../../../molecules/PriceBlock/PaymentBlock';
import {
	IPriceBlockProps,
	PriceBlock,
} from '../../../molecules/PriceBlock/PriceBlock';
import { IPriceStringProps } from '../../../molecules/PriceBlock/PriceString';

import { useLegDetailsContext } from './context';
import { LegPriceWrapper, MiddleCol, RightCol, RouteNumber } from './styled';
import { ILegDetailsProps } from './types';

const renderPaymentParts: IPriceStringProps['render'] = ({
	priceItems,
	previousPrice,
}) => {
	return (
		<FlexContainer testId="payment" gap={4}>
			<Typography
				className="maybeDisabled"
				variant="body2"
				fontWeight={600}
				color="secondary.dark"
				data-testid="price"
			>
				{priceItems[0]}
			</Typography>
			{previousPrice && (
				<Typography
					variant="body2"
					fontWeight={600}
					color="neutral.80"
					sx={{ textDecoration: 'line-through' }}
					data-testid="previousPrice"
				>
					{previousPrice}
				</Typography>
			)}
		</FlexContainer>
	);
};

const renderPricings: IPriceBlockProps['render'] = (children) => {
	return (
		<Typography
			data-testid="pricings"
			variant="body2"
			fontWeight={600}
			color="secondary.dark"
		>
			{children}
		</Typography>
	);
};

export const TransportView: FC<ILegDetailsProps> = ({
	leg,
	isBookedItinerary,
	cancellationFee,
}) => {
	const { t } = useTranslation('common');
	const { disabled } = useLegDetailsContext();

	/** SPECIAL_SCHOOL_TRAFFIC rides have no price — hide all price blocks. */
	const isSpecialSchoolTrafficLeg =
		leg.mode === TripTypeEnum.SPECIAL_SCHOOL_TRAFFIC;
	const hasPricingsBlock =
		!disabled && !isBookedItinerary && !isSpecialSchoolTrafficLeg;
	const hasPaymentBlock =
		!disabled && isBookedItinerary && !isSpecialSchoolTrafficLeg;
	const hasPaidCancellationFee = disabled && (cancellationFee || 0) > 0;

	return (
		<>
			{!disabled && leg.route && (
				<MiddleCol className="routeNumber">
					<RouteNumber>{leg.route}</RouteNumber>
				</MiddleCol>
			)}
			<RightCol>
				<FlexColumn>
					<Typography
						data-testid="TransportView-travelTypeName"
						className="maybeDisabled"
						variant="body2"
					>
						{leg.lbodtflexLabel ||
							t(`travelTypeName.${leg.vehicleType || leg.mode}`)}
					</Typography>
					{leg.headsign && (
						<Typography variant="body2" color="neutral.80">
							{leg.headsign}
						</Typography>
					)}
					{leg.agencyName && (
						<Typography variant="caption" color="neutral.80">
							{t('tickets:text.operator', { operator: leg.agencyName })}
						</Typography>
					)}
					<LegPriceWrapper>
						{hasPaymentBlock && (
							<PaymentBlock
								entity={leg}
								single
								renderPaymentParts={renderPaymentParts}
								render={identity}
							/>
						)}
						{hasPaidCancellationFee && (
							<PaymentBlock
								entity={leg}
								single
								renderPaymentParts={renderPaymentParts}
								totalCancellationPrice={cancellationFee}
								isCancelled={disabled}
								render={identity}
							/>
						)}
						{hasPricingsBlock && (
							<PriceBlock render={renderPricings} legs={leg} />
						)}
					</LegPriceWrapper>
				</FlexColumn>
			</RightCol>
		</>
	);
};
